import { useState, useCallback, useEffect, useRef } from "react";
import BseContext from "./BseContext";
import { getOrderSession } from "../../api/bseApi";

const BSE_SESSION_KEY = "bse_session";

/** Minutes before expiry to auto-refresh session (BSE session valid 1 hour) */
const REFRESH_BEFORE_EXPIRY_MIN = 10;

/**
 * BSE State - Manages BSE StAR MF session (login)
 * Session persists in localStorage, valid 1 hour. Auto-refreshes before expiry.
 * Admin login stays connected - no logout from UI.
 */
const BseState = (props) => {
  const [bseSession, setBseSession] = useState(() => {
    try {
      const stored = localStorage.getItem(BSE_SESSION_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data?.encryptedPassword && data?.expiresAt > Date.now()) {
          return data;
        }
        localStorage.removeItem(BSE_SESSION_KEY);
      }
    } catch (e) {
      localStorage.removeItem(BSE_SESSION_KEY);
    }
    return null;
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Save session to state and localStorage
   */
  const saveSession = useCallback((data) => {
    const { encryptedPassword, passKey, validFor, portalUrl } = data;
    const expiresAt = Date.now() + 55 * 60 * 1000; // 55 min (1 hour - buffer)
    const session = {
      encryptedPassword,
      passKey,
      validFor,
      expiresAt,
      portalUrl: portalUrl || null,
    };
    setBseSession(session);
    localStorage.setItem(BSE_SESSION_KEY, JSON.stringify(session));
    return session;
  }, []);

  /**
   * Login to BSE - gets session token from backend (uses .env credentials)
   * Per BSE APIFileStructure.pdf - getPassword for Order API
   */
  const loginBSE = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOrderSession();
      if (res.data?.status === 1 && res.data?.data) {
        saveSession(res.data.data);
        // Open BSE StAR MF portal in new tab
        const portalUrl = res.data.data?.portalUrl;
        if (portalUrl) {
          window.open(portalUrl, "_blank", "noopener,noreferrer");
        }
        return { success: true };
      } else {
        const msg = res.data?.message || "BSE login failed";
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to connect to BSE";
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, [saveSession]);

  /**
   * Refresh session silently (before expiry). Keeps BSE connected.
   */
  const refreshSession = useCallback(async () => {
    try {
      const res = await getOrderSession();
      if (res.data?.status === 1 && res.data?.data) {
        saveSession(res.data.data);
        setError(null);
        return true;
      }
      return false;
    } catch (err) {
      console.warn("[BSE] Session refresh failed:", err?.response?.data?.message || err.message);
      return false;
    }
  }, [saveSession]);

  /**
   * Open BSE portal in new tab (when already connected)
   */
  const openPortal = useCallback(() => {
    const url = bseSession?.portalUrl || "https://bsestarmf.in/index.aspx";
    window.open(url, "_blank", "noopener,noreferrer");
  }, [bseSession?.portalUrl]);

  /**
   * Logout - clear BSE session (internal use only - not exposed in admin UI)
   */
  const logoutBSE = useCallback(() => {
    setBseSession(null);
    setError(null);
    localStorage.removeItem(BSE_SESSION_KEY);
  }, []);

  const isBseConnected = !!(
    bseSession?.encryptedPassword && bseSession?.expiresAt > Date.now()
  );

  /**
   * Auto-refresh session before expiry - keeps BSE logged in (per BSE API 1hr validity)
   */
  const sessionRef = useRef(bseSession);
  sessionRef.current = bseSession;

  useEffect(() => {
    if (!bseSession?.expiresAt) return;

    const checkAndRefresh = () => {
      const session = sessionRef.current;
      if (!session?.expiresAt) return;
      const now = Date.now();
      const msUntilExpiry = session.expiresAt - now;
      const refreshThreshold = REFRESH_BEFORE_EXPIRY_MIN * 60 * 1000;

      if (msUntilExpiry > 0 && msUntilExpiry < refreshThreshold) {
        refreshSession();
      }
    };

    const interval = setInterval(checkAndRefresh, 2 * 60 * 1000); // Check every 2 min
    checkAndRefresh();

    return () => clearInterval(interval);
  }, [bseSession?.expiresAt, refreshSession]);

  return (
    <BseContext.Provider
      value={{
        bseSession,
        isBseConnected,
        loginBSE,
        logoutBSE,
        refreshSession,
        openPortal,
        loading,
        error,
      }}
    >
      {props.children}
    </BseContext.Provider>
  );
};

export default BseState;
